/**
 * Client portal contracts (/api/portal/*): client-safe projections only —
 * never internal notes, drafts, void documents, payment-source accounts or
 * owner data. Every portal query is scoped by the caller's own client link.
 */

import type {
  ApprovalClientStatus,
  ApprovalCounts,
  ApprovalDecidedVia,
  ApprovalDecision,
  ApprovalVerdict,
} from "../approvals/approvals.types";
import type { ProjectStage } from "../studio/projects.types";
import type { DocumentFolder } from "../workspace/workspace.constants";

/**
 * The 403 error code of a `client`-role account with no portal link yet
 * (e.g. self-registered, not invited): the portal shows "contact the
 * studio" instead of a load failure.
 */
export const PORTAL_NOT_LINKED = "PORTAL_NOT_LINKED";

export interface PortalProjectRef {
  id: string;
  code: string;
  name: string;
  stage: ProjectStage;
}

export interface PortalMe {
  user: { id: string; firstName: string; lastName: string; email: string };
  client: { id: string; displayName: string };
  studio: { name: string; email: string; phone: string; website: string };
  projects: PortalProjectRef[];
}

export interface PortalApprovalItem {
  id: string;
  sortOrder: number;
  title: string;
  description: string | null;
  supplierName: string | null;
  productUrl: string | null;
  imageFileId: string | null;
  room: { name: string } | null;
  quantity: number;
  unitMinor: number | null;
  totalMinor: number | null;
  decision: ApprovalDecision;
  clientComment: string | null;
  decidedAt: string | null;
  decidedVia: ApprovalDecidedVia | null;
  /** The client may still (re)decide: request open, not expired, not bought yet. */
  changeable: boolean;
}

export interface PortalApprovalSummary {
  id: string;
  number: string;
  title: string;
  status: ApprovalClientStatus;
  project: { id: string; code: string; name: string };
  sentAt: string | null;
  expiresOn: string | null;
  counts: ApprovalCounts;
  totalMinor: number;
}

export interface PortalApprovalView extends PortalApprovalSummary {
  message: string | null;
  items: PortalApprovalItem[];
}

export interface PortalDecisionInput {
  decision: ApprovalVerdict;
  comment?: string | null;
}

export interface PortalDocument {
  id: string;
  title: string;
  folder: DocumentFolder;
  project: { id: string; code: string; name: string };
  file: {
    id: string;
    originalName: string;
    mimeType: string;
    sizeBytes: number;
  };
  createdAt: string;
}

export interface PortalMoodboardItem {
  id: string;
  fileId: string;
  caption: string | null;
  sourceUrl: string | null;
  supplier: string | null;
  priceMinor: number | null;
}

export interface PortalMoodboard {
  id: string;
  title: string;
  project: { id: string; code: string; name: string };
  room: { name: string } | null;
  items: PortalMoodboardItem[];
}
